import { mimeTypes } from '@/mime-types'
import { apiCall } from '@/util/apiCall'
import { safeProp } from '@lc/libutils'
import type { Action } from '@reduxjs/toolkit'
import { setAssets, type AssetsSlice } from '../assets'
import { makeLoader, type LoadAPI } from './load'

export function computeAssets(
	manifestAssets: Record<string, string>,
	valkyrieAssets: { path: string; sha1: string }[],
): AssetsSlice {
	const assets: AssetsSlice = {}
	for (const [key, asset] of Object.entries(manifestAssets)) {
		assets[safeProp(key)] = {
			url: asset,
			source: 'default',
		}
	}
	for (const { path, sha1 } of valkyrieAssets) {
		const filename = path.split('?')[0].split('/').at(-1)!
		const type = mimeTypes[filename.split('.').at(-1)!] || undefined
		const params = new URLSearchParams({ sha1 })
		if (type) params.set('type', type)
		let url = '/api/valkyrie/assets/' + path + '?' + params.toString()
		if (process.env.BASE_URL) url = process.env.BASE_URL + url
		assets[safeProp(path)] = {
			url,
			source: 'valkyrie',
		}
	}
	return assets
}

async function loadAssetsImpl(api: LoadAPI): Promise<Action> {
	const [manifestAssets, valkyrieAssets] = await Promise.all([
		(async () => {
			let url = '/assets.json'
			if (process.env.BASE_URL) {
				url = process.env.BASE_URL + url
			}
			const res = await fetch(url, {
				cache: 'no-cache',
				signal: api.signal,
			})
			if (!res.ok) {
				throw new Error(
					`Failed to fetch assets.json: ${res.status} ${res.statusText} ${await res.text()}`,
				)
			}
			return (await res.json()) as Record<string, string>
		})(),
		apiCall<{ path: string; sha1: string }[]>('/valkyrie/assets', {
			signal: api.signal,
		}),
	])
	return setAssets(computeAssets(manifestAssets, valkyrieAssets))
}

async function loadAssetsImplSsr(api: LoadAPI): Promise<Action> {
	const [{ default: assetManifest }, valkyrieAssets] = await Promise.all([
		import('@asset-manifest'),
		apiCall<{ path: string; sha1: string }[]>('/valkyrie/assets', {
			signal: api.signal,
		}),
	])
	return setAssets(computeAssets(assetManifest, valkyrieAssets))
}

export const loadAssets = makeLoader({
	id: 'assets',
	loader: async api => {
		if (process.env.SSR) {
			return await loadAssetsImplSsr(api)
		} else {
			return await loadAssetsImpl(api)
		}
	},
})
